import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import QuestionSetTable from '../../../components/tables/QuestionSetTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { QuestionSetClient, QuestionSetDTO, SearchRequestDTO_questionSetSearchRequestDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/config';
import axiosInstance from '../../../services/axiosInstance';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';

const QuestionSetList: React.FC = () => {
    const [items, setItems] = useState<QuestionSetDTO[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    const navigate = useNavigate();
    const breadcrumbs = [
        { label: 'Admin', href: '#' },
        { label: 'Question Sets', href: '#' },
    ];

    const apiClient = new QuestionSetClient(API_BASE_URL, axiosInstance);

    const fetchItems = async (page: number, size: number, filters = {}) => {
        const searchRequest: SearchRequestDTO_questionSetSearchRequestDTO = {
            filters: {
                name: searchTerm || '',
                ...filters,
            },
            page,
            pageSize: size,
            sortBy: 'CreatedUtc',
            sortDescending: true,
        };

        try {
            setIsLoading(true);
            const response = await apiClient.searchQuestionSets(searchRequest);
            const totalRecords = response.data?.totalCount ?? 0;

            setItems(response.data?.results as QuestionSetDTO[] || []);
            setTotalRecords(totalRecords);
            setTotalPages(Math.ceil(totalRecords / size));
        } catch (error) {
            console.error(`Error fetching QuestionSets:`, error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchItems(currentPage, pageSize);
    }, [currentPage, pageSize]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchItems(1, pageSize);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        fetchItems(1, pageSize, { name: '' });
    };

    const handleDelete = async (dto: QuestionSetDTO) => {
        const confirmed = await confirmDelete();
        if (!confirmed) return;

        try {
            await apiClient.deleteQuestionSet(dto.id || '');
            await fetchItems(currentPage, pageSize, { searchTerm });
        } catch (error) {
            console.error('Error deleting:', error);
        }
    };

    return (
        <AdminLayout
            title="Question Sets"
            breadcrumbs={breadcrumbs}
            allowedRoles={['Administrator']}
        >
            <Card>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-500 dark:text-gray-300">
                        Manage Question Sets ({totalRecords})
                    </h2>
                    <div className="ml-auto">
                        <AddNewButton onClick={() => navigate('/admin/questionsets/create')} />
                    </div>
                </div>

                <form onSubmit={handleSearch} className="mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <FormField
                            id="searchTerm"
                            label="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name"
                        />
                    </div>

                    <div className="flex space-x-4 mb-4">
                        <Button variant="secondary" onClick={handleClearSearch}>
                            Clear Search
                        </Button>
                        <Button type="submit">Search</Button>
                    </div>
                </form>

                <QuestionSetTable
                    data={items}
                    handleEdit={(dto) =>
                        navigate(`/admin/questionsets/${dto.id}`)
                    }
                    handleDelete={handleDelete}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    isLoading={isLoading}
                />
            </Card>
        </AdminLayout>
    );
};

export default QuestionSetList;
